import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserRole } from '../../redux/authSlice';
import {
  Home,
  User,
  FileText,
  CreditCard,
  Send,
  List,
  Lock,
  ShieldCheck,
  ChevronRight,
} from 'lucide-react';

interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  requiredRole: UserRole;
  hash?: string;
}

interface CustomerSidebarProps {
  isOpen?: boolean;
  onNavigate?: () => void;
}

const CustomerSidebar: React.FC<CustomerSidebarProps> = ({ isOpen = false, onNavigate }) => {
  const location = useLocation();

  const menuItems: MenuItem[] = [
    { path: '/dashboard/home', label: 'Home', icon: <Home size={17} />, requiredRole: 'user' },
    { path: '/dashboard/profile', label: 'Profile', icon: <User size={17} />, requiredRole: 'user' },
    { path: '/dashboard/profile', label: 'Two-Factor', icon: <ShieldCheck size={17} />, requiredRole: 'user', hash: '#two-factor' },
    { path: '/dashboard/documents', label: 'Documents', icon: <FileText size={17} />, requiredRole: 'user' },
    { path: '/dashboard/funding', label: 'Fund Account', icon: <CreditCard size={17} />, requiredRole: 'user' },
    { path: '/dashboard/payments', label: 'New Payment', icon: <Send size={17} />, requiredRole: 'user' },
    { path: '/dashboard/my-payments', label: 'My Payments', icon: <List size={17} />, requiredRole: 'user' },
    { path: '/dashboard/change-password', label: 'Security', icon: <Lock size={17} />, requiredRole: 'user' },
  ];

  const isActive = (item: MenuItem) => {
    if (location.pathname !== item.path) return false;
    if (item.hash) {
      return location.hash === item.hash;
    }
    return !location.hash;
  };

  return (
    <>
      {/* Sidebar Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-fintech-text/30 backdrop-blur-sm lg:hidden"
          onClick={onNavigate}
        />
      )}

      <aside
        className={`
          enterprise-sidebar enterprise-sidebar--user fixed top-0 left-0 h-full z-30 w-72 sm:w-64 flex flex-col
          transition-transform duration-300 ease-in-out shadow-2xl
          lg:relative lg:translate-x-0 lg:z-auto
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/10 to-black/10 pointer-events-none lg:hidden" />
        {/* Sidebar Header */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-blue-800">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">K</span>
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-tight">Kenluk Pay</p>
            <p className="text-blue-300 text-xs">Customer Portal</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-hide">
          <p className="text-blue-400 text-xs font-semibold uppercase tracking-wider px-3 mb-2">
            Navigation
          </p>
          <ul className="flex flex-col gap-0.5">
            {menuItems.map((item) => (
              <li key={`${item.path}${item.hash ?? ''}`}>
                <Link
                  to={item.hash ? { pathname: item.path, hash: item.hash } : item.path}
                  onClick={onNavigate}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    transition-all duration-150 no-underline group
                    ${isActive(item)
                      ? 'bg-white/15 text-white'
                      : 'text-blue-200 hover:bg-white/10 hover:text-white'
                    }
                  `}
                >
                  <span className={`flex-shrink-0 ${isActive(item) ? 'text-white' : 'text-blue-300 group-hover:text-white'}`}>
                    {item.icon}
                  </span>
                  <span className="flex-1 font-semibold lg:font-medium">{item.label}</span>
                  {isActive(item) && (
                    <ChevronRight size={14} className="text-white/60" />
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Sidebar Footer */}
        <div className="px-4 py-4 border-t border-blue-800">
          <div className="flex items-center gap-2 text-xs text-blue-400">
            <div className="w-1.5 h-1.5 rounded-full bg-fintech-accent animate-pulse" />
            <span>System operational</span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default CustomerSidebar;
